import {CardData} from '../../../../../data/active_data/field_config/card_stack_data/CardData';
import {DraggableObject} from '../../../../../base/DraggableObject';
import {CardView} from './card/CardView';
import {TextureUrlLoader} from '../../../../../utils/TextureUrlLoader';
import {InteractionEvent, Point, Texture} from 'pixi.js';
import {gsap, Sine} from 'gsap';
import {CardEvents} from './card/CardEvents';
import {CardSelector} from '../../../../../controllers/CardSelector';
import {IPoint} from '../../../../../interfaces/primitives/IPoint';
import {HandController} from '../../../../../controllers/HandController';
import MessageHelper from '../../../../../../../../helpers/MessageHelper';
import {Stack} from './Stack';
import {StackSelector} from '../../../../../controllers/StackSelector';
import {CardDataEvent} from '../../../../../data/active_data/field_config/card_stack_data/card_data/CardDataEvent';
import {DynamicData} from '../../../../../data/DynamicData';
import {HandEvents} from '../../../hand/HandEvents';
import {ApiSendCommandsController} from '../../../../../controllers/ApiSendCommandsController';
import {ActiveData} from '../../../../../data/ActiveData';
import {StaticData} from '../../../../../data/StaticData';
import {TableController} from '../../../../../controllers/silent/TableController';
import {CardStackData, ICardStackData} from '../../../../../data/active_data/field_config/CardStackData';

export class Card extends DraggableObject
{
    sourceCard: Card | undefined;
    mainDragging: boolean = false;
    sourceStack: Stack | undefined;
    tempHoldingStack: Stack | undefined;
    private view: CardView | undefined;
    private hoverTween: gsap.core.Tween | undefined;
    private standSitTween: gsap.core.Tween | undefined;
    private frontTexture: Texture | undefined | null;
    private backTexture: Texture | undefined | null;
    private flipTween: gsap.core.Tween | undefined;
    private _shown: boolean = true;
    private goXtween: gsap.core.Tween | undefined;

    constructor(public activeData: CardData) {
        super();

        this.activeData.addListener(CardDataEvent.FLIPPED_CHANGED, this.onFlipped, this);
    }

    private _goX: number = 0;

    set goX(value: number) {
        this.x = value;

        /*if (this._goX == value) {
            return;
        }
        this._goX = value;
        this.goXtween && this.goXtween.pause();
        this.goXtween = gsap.to(this, {
            x: value,
            duration: .1,
            ease: Sine.easeInOut
        });*/
    };

    private _hei: number = 0;

    get hei(): number {
        return this._hei;
    }

    private _wid: number = 0;

    get wid(): number {
        return this._wid;
    }

    async onFlipped(): Promise<void> {
        //CardSelector.unselectAll(false);
        this.interactive = false;
        this.flipTween && this.flipTween.pause();
        await new Promise(resolve => this.flipTween = gsap.to(this.view!, {
            flipCorner: Math.PI,
            duration: .5,
            ease: Sine.easeInOut,
            onComplete: resolve
        }));
        await this.initialize();
        this.interactive = true;
    }

    async initialize(): Promise<void> {
        let viewHoverScale: number = 1;
        if (this.view) {
            viewHoverScale = this.view.hoverScale;
            this.removeChild(this.view);
            this.view.destroy();
        }

        this.frontTexture = await TextureUrlLoader.load(this.activeData.flipped ? this.activeData.backface_src : this.activeData.face_src);
        this.backTexture = await TextureUrlLoader.load(this.activeData.flipped ? this.activeData.face_src : this.activeData.backface_src);
        this.view = new CardView(this.activeData, this.frontTexture!, this.backTexture!);
        this.view.hoverScale = viewHoverScale;
        this.addChild(this.view);
        this.scaleToHeight(this._hei);
    }

    scaleToHeight(height: number) {
        this._hei = height;
        let scale: number = this.view!.scaleToHeight(height);
        this._wid = this.activeData.width * scale;
        this.y = height;
        this.view!.pivot.y = this.activeData.height;
    }

    /**
     * Обработчик события pointerover
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOver(e: InteractionEvent): void {
        let mainDraggingCard: Card | undefined = CardSelector.selectedCards.find(card => card.mainDragging);
        if (CardSelector.selected(this) && mainDraggingCard) {
            dispatchEvent(new MessageEvent(HandEvents.SORT_CARDS, {data: {card: mainDraggingCard}}));
            return;
        }
        if (DynamicData.dragSomething && !StackSelector.selectedStacks.length && mainDraggingCard && !mainDraggingCard.dragged) {
            return;
        }
        CardSelector.highlight(this);
        if (DynamicData.dragSomething) {
            if (CardSelector.selectedCards.length && this.activeData.id != -1) {
                dispatchEvent(new MessageEvent(HandEvents.DRAG_SELECTED_CARDS_OVER_ANOTHER_CARD, {data: {overCard: this}}));
            } else if (StackSelector.selectedStacks.length) {
                dispatchEvent(new MessageEvent(HandEvents.DRAG_SELECTED_STACKS_OVER_CARD, {data: {overCard: this}}));
            }
            return;
        }

        this.makeBigger();
        this.standUp();
    }

    /**
     * Обработчик события pointerout
     * @param e Событие взаимодейтсвия пользователя
     */
    onPointerOut(e: InteractionEvent): void {
        CardSelector.unhighlight();

        if (!CardSelector.selected(this) && this._shown && this.activeData.id != -1) {
            if (DynamicData.dragSomething) {
                CardSelector.selectedCards.forEach(card => card.sourceCard!.makeBigger(false));
            } else {
                this.sitDown();
            }
        }
    }

    /**
     * Применяем новые координаты мыши
     */
    tryMove(): void {
        let x: number = this.movePosition.x - this.pointerStartOffset.x;
        let y: number = this.movePosition.y - this.pointerStartOffset.y;
        let deltaMove: IPoint = this.clampPos(x, y);
        let result: IPoint = {x: deltaMove.x - this.x, y: deltaMove.y - this.y};
        this.startMoveCoors.x += result.x;
        this.startMoveCoors.y += result.y;

        if (!this.dragged && (Math.abs(this.startMoveCoors.x) > StaticData.dragSilentArea || Math.abs(this.startMoveCoors.y) > StaticData.dragSilentArea)) {
            this.dragged = true;
        }

        if (this.dragged) {
            CardSelector.moveSelectedOnBy(result);
        }
    }

    /**
     * Обработчик события pointermove сцены
     * @param e Событие взаимодейтсвия пользователя
     */
    onDragMove(e: InteractionEvent): void {
        this.movePosition = this.parent.toLocal(e.data.global, void 0);
        this.tryMove();
        if (this.tempHoldingStack) {
            this.tempHoldingStack.onDragMove(e);
        }
    }

    /**
     * Обработчик события onrightup
     * @param e Событие взаимодейтсвия пользователя
     */
    async onRightUp(e: InteractionEvent): Promise<void> {
        if (!this.dragged) {
            dispatchEvent(new MessageEvent(CardEvents.ON_CARD_RIGHT_UP, {data: {card: this, event: e}}));
        }

        await this.onPointerUp(e, true);
        super.onRightUp(e);
    }

    /**
     * Обработчик события pointerdown
     * @param e Событие взаимодейтсвия пользователя
     */
    async onPointerDown(e: InteractionEvent): Promise<void> {
        //Если карта не виртуальная
        if (!this.sourceCard) {
            e.stopPropagation();

            if (!CardSelector.selected(this) && !e.data.originalEvent.shiftKey) {
                CardSelector.unselectAll();
            }

            let selectedCards: Card[] = CardSelector.selectedCards.concat();
            if (!CardSelector.selected(this)) {
                selectedCards.push(this);
                this.standUp(true);
            }
            let tempCards: Card[] = [];
            let tempCard: Card;
            let selectedCard: Card;
            CardSelector.unselectAll(false);
            CardSelector.unhighlight();
            for (let selectedCardIndex: number = 0; selectedCardIndex < selectedCards.length; selectedCardIndex++) {
                selectedCard = selectedCards[selectedCardIndex];
                tempCard = await selectedCard.takeCardSilently();
                tempCard.sourceCard = selectedCard;
                dispatchEvent(new MessageEvent(CardEvents.ON_HAND_TAKE_VIRTUAL_CARDS, {data: {cards: [tempCard]}}));
                tempCard.interactive = tempCard.interactiveChildren = false;
                tempCard.hide();
                selectedCard.standUp(true);
                CardSelector.selectCard(tempCard, false);
                tempCard.mainDragging = false;
                if (selectedCard === this) {
                    tempCard.mainDragging = true;
                    await tempCard.onPointerDown(e);
                }
                tempCards.push(tempCard);
            }
            CardSelector.setTempCardsDragIndexes(tempCards);
            //в исходной колоде больше не делаем ничего
            return;
        }

        //Начинаем таскать виртуальную карту
        StackSelector.unselectAll();
        super.onPointerDown(e);
        dispatchEvent(new MessageEvent(CardEvents.ON_CARD_POINTER_DOWN, {data: {card: this}}));
        e.stopPropagation();
    }

    /**
     * Обработчик события pointerup
     * @param e Событие взаимодейтсвия пользователя
     * @param right правая кнопка мыши
     */
    async onPointerUp(e: InteractionEvent, right: boolean = false): Promise<void> {
        //запоминаем была ли колода перемещена, т.к. после super.onPointerUp(e), this.dragged будет false
        let dragged: boolean = this.dragged;
        super.onPointerUp(e);
        //никакие поднятия над картами кроме как из виртуальных карт не слушаем
        if (!this.sourceCard) {
            return;
        }

        let highlightedCard: Card | null = CardSelector.highlighted();
        let highlightedStack: Stack | null = StackSelector.highlighted();

        let selectedCards: Card[] = CardSelector.selectedCards.concat();
        CardSelector.unselectAll(false);
        dispatchEvent(new MessageEvent(CardEvents.ON_HAND_RETURN_VIRTUAL_CARDS, {data: {cards: selectedCards}}));

        DynamicData.holdingTempStacksCreated = false;

        if (dragged) {
            if (selectedCards.find(selectedCard => selectedCard.sourceCard === highlightedCard) || HandController.handHighLighted) {
                this.showSourceCardsOf(selectedCards);
                selectedCards.filter(selectedCard => selectedCard.sourceCard != highlightedCard).forEach(selectedCard => selectedCard.sourceCard!.sitDown());
                selectedCards.find(selectedCard => selectedCard.mainDragging)!.sourceCard!.makeBigger();
                ApiSendCommandsController.changeHandItemsSort(ActiveData.root.getCurrentPlayerData().cards.classes.map(cardData => cardData.id), []);
                this.finalizeDragging(selectedCards, e.data.originalEvent.shiftKey, true, false);
            } else if (highlightedStack) {
                this.showSourceCardsOf(selectedCards);
                this.putVirtualCardToHighLightedStack(selectedCards, highlightedStack, right);
                this.finalizeDragging(selectedCards, e.data.originalEvent.shiftKey, true, false);
            } else {
                selectedCards.forEach(card => card.tempHoldingStack!.tempOnTable = true);
                let minX: number = Number.MAX_VALUE;
                let minY: number = Number.MAX_VALUE;
                selectedCards.forEach(card => {
                    minX = Math.min(card.tempHoldingStack!.x, minX);
                    minY = Math.min(card.tempHoldingStack!.y, minY);
                });
                this.putVirtualCardsToTable(selectedCards, minX, minY, right);
                this.finalizeDragging(selectedCards, e.data.originalEvent.shiftKey, false, false);
            }
            StackSelector.unselectAll(true);
            return;
        }
        this.showSourceCardsOf(selectedCards);
        this.finalizeDragging(selectedCards, e.data.originalEvent.shiftKey);
    }

    /**
     * Ограничение перемещения внутри области перетаскивания
     * @param x - желаемая координата по горизонтали
     * @param y - желаемая координата по вертикали
     * @returns объект содержащий возможные координаты, полученные из желаемых
     */
    clampPos(x: number, y: number): IPoint {
        return {x, y};
    }

    hide(): void {
        this.visible = false;
        //this.alpha = .2;
        //this.interactive = false;
        this._shown = false;
    }

    show(): void {
        this.visible = true;
        //this.alpha = 1;
        //this.interactive = true;
        this._shown = true;
    }

    destroy(removeTempHoldingStack: boolean = true): void {
        removeTempHoldingStack && this.tryRemoveTempHoldingStack();
        CardSelector.unselectCard(this);
        this.activeData.removeListener(CardDataEvent.FLIPPED_CHANGED, this.onFlipped, this);

        this.standSitTween && this.standSitTween.pause();
        this.goXtween && this.goXtween.pause();
        this.hoverTween && this.hoverTween.pause();

        delete this.sourceCard;
        delete this.hoverTween;
        delete this.goXtween;
        delete this.standSitTween;
        delete this.frontTexture;
        delete this.backTexture;
        delete this.flipTween;

        super.destroy();
    }

    sitDown(quick: boolean = false): void {
        this.standSitTween && this.standSitTween.pause();
        if (quick) {
            this.pivot.y = 0;
            return;
        }

        this.makeBigger(false);

        this.standSitTween = gsap.to(this.pivot, {
            y: 0,
            duration: .2,
            ease: Sine.easeInOut
        });
    }

    createHoldingTempStack(): void {
        let config: ICardStackData = {
            angle: 0,
            blockedByPlayer: '',
            cards: [this.activeData.config!],
            flippedFor: [],
            hiddenFor: [],
            invisibleFor: [],
            visibleFor: [],
            id: -1,//временная виртуальная колода, о которой не знает сервер
            locked: false,
            masterLocked: false,
            tags: [],
            title: '',
            usedByPlayer: '',
            width: this.activeData.width,
            height: this.activeData.height,
            x: 0,
            y: 0,
            z: 0
        };
        let cardStackData: CardStackData = new CardStackData(config);
        this.tempHoldingStack = new Stack(cardStackData);
        this.tempHoldingStack.initialize();
        let mainDraggingCard: Card = CardSelector.selectedCards.find(card => card.mainDragging)!;
        let index: number = CardSelector.dragIndexByTempCardNormalized.get(this)!;
        let coors: Point = TableController.globalToTable({
            x: mainDraggingCard.x + mainDraggingCard.pointerStartOffset.x,
            y: mainDraggingCard.y + mainDraggingCard.pointerStartOffset.y
        });
        this.tempHoldingStack.x = coors.x - mainDraggingCard.pointerStartOffset.x / this.view!.scale.x + index * (this.activeData.width + 20);
        this.tempHoldingStack.y = coors.y - (mainDraggingCard.pointerStartOffset.y + this.hei) / this.view!.scale.x;
        this.tempHoldingStack.interactive = this.tempHoldingStack.interactiveChildren = false;
        this.tempHoldingStack.copyDragParams({
            x: mainDraggingCard.pointerStartOffset.x / this.view!.scale.x,
            y: (mainDraggingCard.pointerStartOffset.y + mainDraggingCard.hei) / this.view!.scale.x
        }, this.startMoveCoors, true);
        TableController.holdingStacks.addStack(this.tempHoldingStack);
        ActiveData.root.card_stacks.setAt(ActiveData.root.card_stacks.length, this.tempHoldingStack.activeData, this.tempHoldingStack.activeData.config!);
        ActiveData.hideEvents();
        ActiveData.dispatchEventsPool();
        ActiveData.showEvents();
        StackSelector.selectStack(this.tempHoldingStack, false);
    }

    showHoldingTempStack(): void {
        this.tempHoldingStack!.visible = true;
        this.sourceCard!.hide();
    }

    hideHoldingTempStack(): void {
        this.tempHoldingStack!.visible = false;
        this.sourceCard!.show();
    }

    standUp(quick: boolean = false): void {
        this.standSitTween && this.standSitTween.pause();
        if (quick) {
            this.pivot.y = StaticData.handCardStandUpOffset;
            return;
        }
        this.standSitTween = gsap.to(this.pivot, {
            y: StaticData.handCardStandUpOffset,
            duration: .2,
            ease: Sine.easeInOut
        });
    }

    private showSourceCardsOf(selectedCards: Card[]): void {
        selectedCards.forEach(selectedCard => {
            selectedCard.sourceCard!.show();
        });
    }

    private finalizeDragging(selectedCards: Card[], shiftKey: boolean, removeTempHoldingStacks: boolean = true, selectCard: boolean = true): void {
        selectedCards.forEach(selectedCard => {
            removeTempHoldingStacks && selectedCard.sourceCard!.tryRemoveTempHoldingStack();
            selectCard && CardSelector.selectCard(selectedCard.sourceCard!, !shiftKey);
            selectedCard.sourceCard = undefined;
            selectedCard.destroy(removeTempHoldingStacks);
        });
    }

    private onUpdate(): void {
        dispatchEvent(new MessageEvent(CardEvents.SCALE_CHANGED, {data: {}}));
    }

    /**
     * Взять карту из колоды, фактически создаём колоду из одной карты
     * помещаем в неё первую или последнюю карту в зависимости от потого перевёрнута ли исходная
     * ожидаем события создания колоды на столе
     */
    private async takeCardSilently(): Promise<Card> {
        let card: Card = new Card(this.activeData);
        await card.initialize();
        card.scaleToHeight(this.view!.height);
        card.x = this.x;
        card.y = this.y - this.pivot.y;
        return card;
    }

    private putVirtualCardsToTable(cards: Card[], x: number, y: number, right: boolean) {
        {
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_CARD_TO_TABLE_FROM_HAND, {
                x,
                y,
                ids: cards.map(card => card.activeData.id),
                delta: cards[0].tempHoldingStack!.activeData.angle
            });
            !right && CardSelector.unselectAll();
        }
    }

    private putVirtualCardToHighLightedStack(cards: Card[], highlighted: Stack, right: boolean) {
        if (!StackSelector.selected(highlighted)) {
            MessageHelper.sendChangeCardsMessage(MessageHelper.TYPE_MOVE_SOLO_CARD_TO_STACK, {
                ids: cards.map(card => card.activeData.id),
                stackId: highlighted.activeData.id,
                flip: false
            });
        }
        !right && CardSelector.unselectAll();
    }

    private tryRemoveTempHoldingStack(): void {
        if (!this.tempHoldingStack) {
            return;
        }
        TableController.holdingStacks.removeStack(this.tempHoldingStack);
        this.tempHoldingStack.destroy();
        delete this.tempHoldingStack;
    }

    private makeBigger(value: boolean = true): void {
        this.hoverTween && this.hoverTween.pause();
        this.hoverTween = gsap.to(this.view!, {
            hoverScale: value ? StaticData.handCardBiggerScale : 1,
            duration: .2,
            ease: Sine.easeInOut,
            onUpdate: () => {
                this.onUpdate();
                value && dispatchEvent(new MessageEvent(HandEvents.SORT_CARDS, {data: {card: this}}));
            }
        });
    }
}
